import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuChevronLeft,
  LuChevronRight,
  LuCopy,
  LuDownload,
  LuEllipsisVertical,
  LuEye,
  LuPencil,
  LuPlus,
  LuPower,
  LuSearch,
  LuShield,
  LuTrash2,
  LuUserCheck,
  LuUsers,
  LuFileText,
} from "react-icons/lu";
import {
  accessExtractMessage,
  createAccessRole,
  deleteAccessRole,
  fetchAccessRoleById,
  fetchAccessRoles,
  updateAccessRole,
} from "../../services/superAdminAccess.service";
import "../../assets/styles/super_admin_access.scss";

const PAGE_OPTIONS = [5, 10, 20];

const ROLE_CATEGORY_LABELS = {
  platform: "Platform Role",
  support_manager: "Support Manager Role",
  logistics_manager: "Logistics Manager Role",
  support_user: "Support User Role",
  logistics_user: "Logistics User Role",
};

const formatRoleCategory = (roleLevel) =>
  ROLE_CATEGORY_LABELS[roleLevel] || "Platform Role";

const countGrantedPermissions = (permissions = {}) =>
  Object.values(permissions).reduce((total, actions) => {
    if (!actions || typeof actions !== "object") return total;
    return (
      total +
      Object.values(actions).filter(Boolean).length
    );
  }, 0);

export default function SuperAdminRoleManagementPage({ basePath = "/super-admin" }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState(() => searchParams.get("type") || "all");
  const [categoryFilter, setCategoryFilter] = useState(
    () => searchParams.get("category") || "all",
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [busyRoleId, setBusyRoleId] = useState(null);
  const [viewRole, setViewRole] = useState(null);

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAccessRoles({
        search: query.trim() || undefined,
        type: typeFilter === "all" ? undefined : typeFilter,
        roleLevel: categoryFilter === "all" ? undefined : categoryFilter,
      });
      setRoles(Array.isArray(data.items) ? data.items : []);
    } catch (error) {
      toast.error(accessExtractMessage(error, "Failed to load roles."));
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, query, typeFilter]);

  useEffect(() => {
    setTypeFilter(searchParams.get("type") || "all");
    setCategoryFilter(searchParams.get("category") || "all");
    setPage(1);
  }, [searchParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadRoles();
    }, 250);
    return () => clearTimeout(timer);
  }, [loadRoles]);

  useEffect(() => {
    if (openMenuId == null) return undefined;

    const onPointerDown = (event) => {
      const target = event.target;
      if (
        !(target instanceof Element) ||
        !target.closest(`[data-role-menu="${openMenuId}"]`)
      ) {
        setOpenMenuId(null);
        setMenuPosition(null);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpenMenuId(null);
        setMenuPosition(null);
      }
    };
    const onViewportChange = () => {
      setOpenMenuId(null);
      setMenuPosition(null);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
    };
  }, [openMenuId]);

  const filtered = roles;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);
  const openMenuRole = roles.find((role) => role.id === openMenuId) || null;

  const stats = useMemo(() => {
    const total = roles.length;
    const custom = roles.filter((role) => role.type === "Custom").length;
    const system = roles.filter((role) => role.type === "System").length;
    const inUse = roles.filter((role) => role.usersAssigned > 0).length;
    return { total, custom, system, inUse };
  }, [roles]);

  const closeMenu = () => {
    setOpenMenuId(null);
    setMenuPosition(null);
  };

  const toggleMoreMenu = (event, roleId) => {
    if (openMenuId === roleId) {
      closeMenu();
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const panelHeight = 180;
    const panelWidth = 190;
    const gap = 6;
    const fitsBelow = window.innerHeight - rect.bottom >= panelHeight + gap;
    const top = fitsBelow
      ? rect.bottom + gap
      : Math.max(8, rect.top - panelHeight - gap);
    const left = Math.min(
      window.innerWidth - panelWidth - 8,
      Math.max(8, rect.right - panelWidth),
    );

    setMenuPosition({ top, left });
    setOpenMenuId(roleId);
  };

  const handleViewDetails = async (role) => {
    closeMenu();
    setBusyRoleId(role.id);
    try {
      const fullRole = await fetchAccessRoleById(role.id);
      setViewRole(fullRole || role);
    } catch (error) {
      toast.error(accessExtractMessage(error, "Failed to load role details."));
    } finally {
      setBusyRoleId(null);
    }
  };

  const handleCloneRole = async (role) => {
    closeMenu();
    setBusyRoleId(role.id);
    try {
      const fullRole = await fetchAccessRoleById(role.id);
      if (!fullRole) {
        toast.error("Role not found.");
        return;
      }
      await createAccessRole({
        name: `Copy of ${fullRole.name}`,
        description: fullRole.description || "",
        roleLevel: fullRole.roleLevel || "platform",
        scope: fullRole.scope || "platform",
        status: "Active",
        tone: fullRole.tone || "orange",
        permissions: fullRole.permissions || {},
      });
      toast.success("Role cloned successfully.");
      await loadRoles();
    } catch (error) {
      toast.error(accessExtractMessage(error, "Failed to clone role."));
    } finally {
      setBusyRoleId(null);
    }
  };

  const handleToggleStatus = async (role) => {
    closeMenu();
    const nextStatus = role.status === "Active" ? "Inactive" : "Active";
    setBusyRoleId(role.id);
    try {
      await updateAccessRole(role.id, { status: nextStatus });
      toast.success(
        nextStatus === "Active" ? "Role activated." : "Role deactivated.",
      );
      await loadRoles();
    } catch (error) {
      toast.error(accessExtractMessage(error, "Failed to update role status."));
    } finally {
      setBusyRoleId(null);
    }
  };

  const handleDeleteRole = async (role) => {
    closeMenu();
    if (role.type === "System") {
      toast.error("System roles cannot be deleted.");
      return;
    }
    if (Number(role.usersAssigned || 0) > 0) {
      toast.error("Role is assigned to users and cannot be deleted.");
      return;
    }
    const confirmed = window.confirm(
      `Delete role "${role.name}"? This action cannot be undone.`,
    );
    if (!confirmed) return;

    setBusyRoleId(role.id);
    try {
      await deleteAccessRole(role.id);
      toast.success("Role deleted successfully.");
      await loadRoles();
    } catch (error) {
      toast.error(accessExtractMessage(error, "Failed to delete role."));
    } finally {
      setBusyRoleId(null);
    }
  };

  return (
    <div className="sa-access-page">
      <header className="sa-access-head">
        <div>
          <h1>Role Management</h1>
          <p>Create and manage roles with permission controls across modules.</p>
        </div>
        <div className="sa-access-actions">
          <button
            type="button"
            className="sa-access-btn sa-access-btn--ghost"
            onClick={() => {
              const payload = roles.map((role) => ({
                id: role.id,
                name: role.name,
                description: role.description,
                type: role.type,
                status: role.status,
                usersAssigned: role.usersAssigned,
                scope: role.scope,
              }));
              const blob = new Blob([JSON.stringify(payload, null, 2)], {
                type: "application/json",
              });
              const url = URL.createObjectURL(blob);
              const anchor = document.createElement("a");
              anchor.href = url;
              anchor.download = "super-admin-roles.json";
              anchor.click();
              URL.revokeObjectURL(url);
              toast.success("Roles exported.");
            }}
          >
            <LuDownload aria-hidden /> Export
          </button>
          <Link to={`${basePath}/roles/create`} className="sa-access-btn sa-access-btn--primary">
            <LuPlus aria-hidden /> Create Role
          </Link>
        </div>
      </header>

      <section className="sa-access-stats">
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--blue"><LuUsers /></span>
          <div>
            <strong>{Number(stats.total).toLocaleString()}</strong>
            <span>Total Roles</span>
          </div>
        </article>
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--green"><LuUserCheck /></span>
          <div>
            <strong>{Number(stats.custom).toLocaleString()}</strong>
            <span>Custom Roles</span>
          </div>
        </article>
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--orange"><LuShield /></span>
          <div>
            <strong>{Number(stats.system).toLocaleString()}</strong>
            <span>System Roles</span>
          </div>
        </article>
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--purple"><LuFileText /></span>
          <div>
            <strong>{Number(stats.inUse).toLocaleString()}</strong>
            <span>Roles in Use</span>
          </div>
        </article>
      </section>

      <div className="sa-access-toolbar" style={{ gridTemplateColumns: "minmax(0,1fr) auto auto" }}>
        <label className="sa-access-search">
          <LuSearch aria-hidden />
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder="Search role by name or description..."
          />
        </label>
        <select
          value={categoryFilter}
          onChange={(event) => {
            setCategoryFilter(event.target.value);
            setPage(1);
          }}
          aria-label="Filter by role category"
        >
          <option value="all">All categories</option>
          <option value="platform">Platform Role</option>
          <option value="support_manager">Support Manager Role</option>
          <option value="logistics_manager">Logistics Manager Role</option>
        </select>
        <Link to={`${basePath}/roles/create`} className="sa-access-btn sa-access-btn--primary">
          <LuPlus aria-hidden /> Create Role
        </Link>
      </div>

      <section className="sa-access-card">
        <div style={{ overflowX: "auto" }}>
          <table className="sa-access-table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Description</th>
                <th>Category</th>
                <th>Users Assigned</th>
                <th>Type</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="sa-access-empty">Loading roles...</td>
                </tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="sa-access-empty">No roles found.</td>
                </tr>
              ) : (
                pageRows.map((role) => {
                  const isBusy = busyRoleId === role.id;
                  const isMenuOpen = openMenuId === role.id;
                  return (
                    <tr key={role.id}>
                      <td>
                        <div className="sa-access-role-cell">
                          <span className={`sa-access-role-icon sa-access-badge--${role.tone || "orange"}`}>
                            <LuShield />
                          </span>
                          <strong>{role.name}</strong>
                        </div>
                      </td>
                      <td>{role.description?.trim() || "—"}</td>
                      <td>
                        <span className="sa-access-badge sa-access-badge--blue">
                          {formatRoleCategory(role.roleLevel)}
                        </span>
                      </td>
                      <td>{role.usersAssigned}</td>
                      <td>
                        <span className={`sa-access-badge sa-access-badge--${role.type === "System" ? "purple" : "blue"}`}>
                          {role.type}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`sa-access-badge sa-access-badge--${
                            role.status === "Active" ? "green" : "red"
                          }`}
                        >
                          {role.status}
                        </span>
                      </td>
                      <td>
                        <div className="sa-access-row-actions">
                          <Link
                            to={`${basePath}/roles/${role.id}/edit`}
                            title="Edit"
                            aria-label={`Edit ${role.name}`}
                          >
                            <LuPencil />
                          </Link>
                          <div className="sa-access-more">
                            <button
                              type="button"
                              data-role-menu={role.id}
                              title="More actions"
                              aria-label={`More actions for ${role.name}`}
                              aria-expanded={isMenuOpen}
                              disabled={isBusy}
                              onClick={(event) => toggleMoreMenu(event, role.id)}
                            >
                              <LuEllipsisVertical />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="sa-access-footer">
          <span>
            Showing {filtered.length ? start + 1 : 0} to {Math.min(start + pageSize, filtered.length)} of{" "}
            {filtered.length} roles
          </span>
          <div className="sa-access-pagination">
            <button type="button" disabled={currentPage === 1} onClick={() => setPage((p) => p - 1)}>
              <LuChevronLeft />
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((item) => (
              <button
                key={item}
                type="button"
                className={currentPage === item ? "is-active" : ""}
                onClick={() => setPage(item)}
              >
                {item}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <LuChevronRight />
            </button>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {PAGE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size} / page</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {openMenuRole && menuPosition
        ? createPortal(
            <div
              className="sa-access-more__panel sa-access-more__panel--portal"
              data-role-menu={openMenuRole.id}
              role="menu"
              style={{
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
              }}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => handleViewDetails(openMenuRole)}
              >
                <LuEye aria-hidden /> View Details
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => handleCloneRole(openMenuRole)}
              >
                <LuCopy aria-hidden /> Clone Role
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => handleToggleStatus(openMenuRole)}
              >
                <LuPower aria-hidden />{" "}
                {openMenuRole.status === "Active" ? "Deactivate" : "Activate"}
              </button>
              <button
                type="button"
                role="menuitem"
                className="sa-access-more__danger"
                disabled={openMenuRole.type === "System"}
                onClick={() => handleDeleteRole(openMenuRole)}
              >
                <LuTrash2 aria-hidden /> Delete Role
              </button>
            </div>,
            document.body,
          )
        : null}

      {viewRole ? (
        <div
          className="sa-access-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sa-role-view-title"
          onClick={() => setViewRole(null)}
        >
          <div
            className="sa-access-modal__panel"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sa-access-modal__head">
              <div>
                <h2 id="sa-role-view-title">{viewRole.name}</h2>
                <p>{viewRole.description || "No description provided."}</p>
              </div>
              <button
                type="button"
                className="sa-access-btn sa-access-btn--ghost"
                onClick={() => setViewRole(null)}
              >
                Close
              </button>
            </div>
            <div className="sa-access-modal__grid">
              <div>
                <span>Type</span>
                <strong>{viewRole.type}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong>{viewRole.status}</strong>
              </div>
              <div>
                <span>Category</span>
                <strong>{formatRoleCategory(viewRole.roleLevel)}</strong>
              </div>
              <div>
                <span>Scope</span>
                <strong>{viewRole.scope || "platform"}</strong>
              </div>
              <div>
                <span>Users Assigned</span>
                <strong>{viewRole.usersAssigned || 0}</strong>
              </div>
              <div>
                <span>Permissions Granted</span>
                <strong>{countGrantedPermissions(viewRole.permissions)}</strong>
              </div>
            </div>
            <div className="sa-access-modal__footer">
              <button
                type="button"
                className="sa-access-btn sa-access-btn--ghost"
                onClick={() => setViewRole(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="sa-access-btn sa-access-btn--primary"
                onClick={() => navigate(`${basePath}/roles/${viewRole.id}/edit`)}
              >
                Edit Role
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
